import {
  createInitialShareContext,
  createRecoveryRequestContext,
  fingerprintRecoveryPublicKey,
  generateCustodyKeyPair,
  generateRecoveryKey,
  generateRecoveryKeyPair,
  generateRecoveryTargetKeyPair,
  openRecoveryShareForRequest,
  openRecoveryShareFromCustody,
  recoverAdultVaultKey,
  recoveryRequestReplayKey,
  resealRecoveryShareForRequest,
  sealRecoveryShareForCustody,
  splitRecoveryKey,
  validateRecoveryManifest,
  wrapAdultVaultKey,
  type RecoveryKeyPair,
  type RecoveryManifest,
  type RecoveryPublicKeyJwk,
  type RecoveryReplayGuard,
  type RecoverySealedShare,
} from '../crypto/recovery.ts';
import type { AdultVaultAccess } from '../crypto/adultVault.ts';
import type { RecoveryApiClient } from './api.ts';
import {
  createIndexedDbRecoveryKeyStore,
  isNonExtractablePrivateCryptoKey,
  probeRecoveryKeyPersistence,
  RecoveryKeyStoreError,
  type RecoveryKeyStore,
} from './keyStore.ts';
import type {
  ActivateCircleInput,
  CompleteRecoveryOnTarget,
  GetDeviceCustodyPublicKey,
  GetMembershipCustodyPublicKey,
  GetRecoveryTargetPublicKey,
  GetRequestBoundSealedShare,
  LocalCryptoHealth,
  PrepareCircleActivation,
  RecoveryCircle,
  RecoveryDevice,
  RecoveryInvitation,
  RecoveryRequest,
} from './types.ts';

export interface RecoveryCryptoBridge {
  health: LocalCryptoHealth;
  prepareCircleActivation: PrepareCircleActivation;
  getMembershipCustodyPublicKeyJwk: GetMembershipCustodyPublicKey;
  getDeviceCustodyPublicKeyJwk: GetDeviceCustodyPublicKey;
  getRecoveryTargetPublicKeyJwk: GetRecoveryTargetPublicKey;
  getRequestBoundSealedShare: GetRequestBoundSealedShare;
  completeRecoveryOnTarget: CompleteRecoveryOnTarget;
  afterDeviceRevoked(device: RecoveryDevice): Promise<void>;
  inspectLocalKeys(input: {
    membershipIds?: string[];
    deviceFingerprints?: string[];
  }): Promise<Pick<LocalCryptoHealth, 'missingMembershipIds' | 'missingDeviceFingerprints'>>;
}

export function holderTypeForCrypto(
  holderType: RecoveryCircle['members'][number]['holderType']
): 'person' | 'institution' {
  return holderType === 'institution' ? 'institution' : 'person';
}

export function lockoutRequestType(
  type: RecoveryRequest['type']
): 'owner-lockout' {
  if (type !== 'lockout') {
    throw new Error('Only authenticated lockout drills are available.');
  }
  return 'owner-lockout';
}

export async function buildActivationManifest(
  circle: RecoveryCircle
): Promise<RecoveryManifest> {
  if (circle.ownerUserId == null) {
    throw new Error('Circle activation needs the owner id from the server.');
  }
  if (!circle.createdAt) {
    throw new Error('Circle activation needs the canonical createdAt timestamp.');
  }
  if (!circle.version) {
    throw new Error('Circle activation needs the circle version.');
  }
  const members = [...circle.members].sort(
    (left, right) => (left.position || 0) - (right.position || 0)
  );
  if (
    members.length !== circle.memberCount ||
    members.some((member) => member.status !== 'accepted')
  ) {
    throw new Error('Every declared keyholder must accept before activation.');
  }

  const protocolMembers = [];
  for (const [index, member] of members.entries()) {
    if (!member.custodyPublicKeyJwk) {
      throw new Error(
        'Every accepted keyholder must expose a custody public key to the owner before activation.'
      );
    }
    protocolMembers.push({
      membershipId: member.membershipId || member.id,
      index: member.position || index + 1,
      holderType: holderTypeForCrypto(member.holderType),
      custodyPublicKeyFingerprint: await fingerprintRecoveryPublicKey(
        member.custodyPublicKeyJwk
      ),
    });
  }

  return validateRecoveryManifest({
    protocolVersion: 1,
    circleId: circle.id,
    circleVersion: circle.version,
    ownerId: String(circle.ownerUserId),
    threshold: circle.threshold,
    total: circle.memberCount,
    createdAt: circle.createdAt,
    members: protocolMembers,
  });
}

function zeroBytes(value?: Uint8Array | null): void {
  value?.fill(0);
}

function requireStoreKey(
  record: Awaited<ReturnType<RecoveryKeyStore['getKeyByFingerprint']>>,
  label: string
) {
  if (!record || !isNonExtractablePrivateCryptoKey(record.privateKey)) {
    throw new Error(
      `${label} is not on this browser profile. Another device or profile will not have it, and it will not be regenerated silently.`
    );
  }
  return record;
}

class StagingReplayGuard implements RecoveryReplayGuard {
  readonly staged = new Set<string>();

  constructor(private readonly persisted: Set<string>) {}

  consume(replayKey: string): boolean {
    if (this.persisted.has(replayKey) || this.staged.has(replayKey)) return false;
    this.staged.add(replayKey);
    return true;
  }
}

async function persistGeneratedKey(
  store: RecoveryKeyStore,
  pair: RecoveryKeyPair,
  extra: Omit<
    Parameters<RecoveryKeyStore['putKey']>[0],
    'publicKeyFingerprint' | 'publicKeyJwk' | 'privateKey' | 'createdAt'
  >
): Promise<RecoveryPublicKeyJwk> {
  await store.putKey({
    ...extra,
    publicKeyFingerprint: pair.publicKeyFingerprint,
    publicKeyJwk: pair.publicKeyJwk,
    privateKey: pair.privateKey,
    createdAt: new Date().toISOString(),
  });
  const stored = await store.getKeyByFingerprint(pair.publicKeyFingerprint);
  if (!isNonExtractablePrivateCryptoKey(stored?.privateKey)) {
    await store.deleteKeyByFingerprint(pair.publicKeyFingerprint).catch(() => undefined);
    throw new RecoveryKeyStoreError(
      'This browser did not persist the non-extractable recovery key. The key was not registered.'
    );
  }
  return pair.publicKeyJwk;
}

function requireAdultVault(adultVault?: AdultVaultAccess): AdultVaultAccess {
  if (!adultVault) {
    throw new Error(
      'Adult-chat keys cannot be wrapped until this page is connected to the unlocked chat identity. A dummy key will not be created.'
    );
  }
  return adultVault;
}

export async function createRecoveryCryptoBridge(options?: {
  store?: RecoveryKeyStore;
  apiClient?: RecoveryApiClient;
  adultVault?: AdultVaultAccess;
}): Promise<RecoveryCryptoBridge> {
  const store = options?.store || createIndexedDbRecoveryKeyStore();
  let health: LocalCryptoHealth = {
    supported: true,
    ready: true,
    message:
      'Local recovery keys stay in this browser profile as non-extractable IndexedDB keys. Another profile will not have them.',
    missingMembershipIds: [],
    missingDeviceFingerprints: [],
  };

  try {
    await probeRecoveryKeyPersistence(store);
  } catch (cause) {
    health = {
      supported: false,
      ready: false,
      message:
        cause instanceof Error
          ? cause.message
          : 'Local recovery crypto is unavailable in this browser.',
      missingMembershipIds: [],
      missingDeviceFingerprints: [],
    };
  }

  const requireReady = () => {
    if (!health.supported) {
      throw new Error(health.message);
    }
  };

  const prepareCircleActivation: PrepareCircleActivation = async (circle) => {
    requireReady();
    const manifest = await buildActivationManifest(circle);
    const members = [...circle.members].sort(
      (left, right) => (left.position || 0) - (right.position || 0)
    );
    if (circle.ownerUserId == null) {
      throw new Error('Circle activation needs the owner id from the server.');
    }
    const recoveryKey = generateRecoveryKey();
    let vaultKey: Uint8Array | undefined;
    let sharePackages;
    try {
      vaultKey = await requireAdultVault(options?.adultVault).exportKey(
        String(circle.ownerUserId)
      );
      const vaultEnvelope = await wrapAdultVaultKey(
        vaultKey,
        recoveryKey,
        manifest
      );
      sharePackages = await splitRecoveryKey(recoveryKey, manifest);
      const encryptedShares = [];
      for (const [index, share] of sharePackages.entries()) {
        const member = members[index];
        encryptedShares.push({
          membershipId: member.membershipId || member.id,
          envelope: await sealRecoveryShareForCustody(
            share,
            manifest,
            member.custodyPublicKeyJwk
          ),
        });
      }
      return {
        manifest,
        vaultEnvelope,
        encryptedShares,
      } satisfies ActivateCircleInput;
    } finally {
      zeroBytes(recoveryKey);
      zeroBytes(vaultKey);
      for (const share of sharePackages || []) {
        if (typeof share.shareValue === 'string') {
          (share as { shareValue: string }).shareValue = '';
        }
      }
    }
  };

  const getMembershipCustodyPublicKeyJwk: GetMembershipCustodyPublicKey = async (
    invitation
  ) => {
    requireReady();
    const existing = await store.getKeyByMembershipId(invitation.id);
    if (existing) return existing.publicKeyJwk;
    const pair = await generateCustodyKeyPair();
    return persistGeneratedKey(store, pair, {
      kind: 'membership',
      membershipId: invitation.id,
    });
  };

  const getDeviceCustodyPublicKeyJwk: GetDeviceCustodyPublicKey = async (
    proposedName
  ) => {
    requireReady();
    const pair = await generateRecoveryKeyPair();
    return persistGeneratedKey(store, pair, {
      kind: 'device',
      name: proposedName,
    });
  };

  const getRecoveryTargetPublicKeyJwk: GetRecoveryTargetPublicKey = async (
    device
  ) => {
    requireReady();
    const pair = await generateRecoveryTargetKeyPair();
    return persistGeneratedKey(store, pair, {
      kind: 'target',
      name: device.name,
    });
  };

  const getRequestBoundSealedShare: GetRequestBoundSealedShare = async (
    request
  ) => {
    requireReady();
    const apiClient = options?.apiClient;
    if (!apiClient) {
      throw new Error('Recovery request detail is required to reseal a share.');
    }
    const detail = await apiClient.getRequest(request.id);
    const membershipId = detail.keyholderMembershipId || request.keyholderMembershipId;
    const envelope =
      detail.keyholderEncryptedShare || request.keyholderEncryptedShare;
    const manifest =
      detail.activationManifest ||
      request.activationManifest;
    if (!membershipId || !envelope || !manifest || !detail.targetPublicKeyJwk) {
      throw new Error(
        'This assignment is missing its activation manifest, initial envelope, or target key.'
      );
    }
    const custody = requireStoreKey(
      await store.getKeyByMembershipId(membershipId),
      'The membership custody key'
    );
    const initialContext = await createInitialShareContext(manifest, membershipId);
    const share = await openRecoveryShareFromCustody(
      envelope,
      custody.privateKey,
      initialContext
    );
    try {
      const sealedShare = await resealRecoveryShareForRequest(
        share,
        manifest,
        detail.targetPublicKeyJwk,
        {
          requestId: detail.id,
          requestType: lockoutRequestType(detail.type),
          expiresAt: detail.expiresAt,
        }
      );
      return { requestId: detail.id, sealedShare };
    } finally {
      (share as { shareValue: string }).shareValue = '';
    }
  };

  const completeRecoveryOnTarget: CompleteRecoveryOnTarget = async (
    _request,
    requestDetails
  ) => {
    requireReady();
    const manifest = requestDetails.activationManifest;
    const vaultEnvelope = requestDetails.vaultEnvelope;
    const releases = requestDetails.releases || [];
    if (!manifest || !vaultEnvelope || !requestDetails.targetPublicKeyJwk) {
      throw new Error(
        'Completion needs the activation manifest, vault envelope, and target public key.'
      );
    }
    if (releases.length < manifest.threshold) {
      throw new Error('Not enough request-bound releases are available yet.');
    }
    const targetFingerprint = await fingerprintRecoveryPublicKey(
      requestDetails.targetPublicKeyJwk
    );
    const target = requireStoreKey(
      await store.getKeyByFingerprint(targetFingerprint),
      'The request target key'
    );
    const persisted = new Set<string>();
    const uniqueReleases: Array<{
      membershipId: string;
      sealedShare: RecoverySealedShare;
      replayKey: string;
    }> = [];
    for (const release of releases) {
      const context = await createRecoveryRequestContext(
        manifest,
        release.membershipId,
        requestDetails.targetPublicKeyJwk,
        {
          requestId: requestDetails.id,
          requestType: lockoutRequestType(requestDetails.type),
          expiresAt: requestDetails.expiresAt,
        }
      );
      const replayKey = recoveryRequestReplayKey(context);
      if (await store.hasReplay(replayKey)) {
        persisted.add(replayKey);
      }
      if (uniqueReleases.some((item) => item.membershipId === release.membershipId)) {
        continue;
      }
      uniqueReleases.push({
        membershipId: release.membershipId,
        sealedShare: release.sealedShare,
        replayKey,
      });
    }

    const guard = new StagingReplayGuard(persisted);
    const opened = [];
    for (const release of uniqueReleases) {
      const context = await createRecoveryRequestContext(
        manifest,
        release.membershipId,
        requestDetails.targetPublicKeyJwk,
        {
          requestId: requestDetails.id,
          requestType: lockoutRequestType(requestDetails.type),
          expiresAt: requestDetails.expiresAt,
        }
      );
      opened.push(
        await openRecoveryShareForRequest(
          release.sealedShare,
          target.privateKey,
          context,
          guard
        )
      );
    }

    let recovered: Uint8Array | undefined;
    try {
      recovered = await recoverAdultVaultKey(opened, manifest, vaultEnvelope);
      await requireAdultVault(options?.adultVault).installKey(
        String(manifest.ownerId),
        recovered
      );
      await store.commitReplays([...guard.staged]);
    } finally {
      zeroBytes(recovered);
      for (const share of opened) {
        (share as { shareValue: string }).shareValue = '';
      }
    }
  };

  return {
    health,
    prepareCircleActivation,
    getMembershipCustodyPublicKeyJwk,
    getDeviceCustodyPublicKeyJwk,
    getRecoveryTargetPublicKeyJwk,
    getRequestBoundSealedShare,
    completeRecoveryOnTarget,
    async afterDeviceRevoked(device) {
      if (device.fingerprint) {
        await store.deleteKeyByFingerprint(device.fingerprint);
      }
    },
    async inspectLocalKeys({ membershipIds = [], deviceFingerprints = [] }) {
      const missingMembershipIds = [];
      for (const membershipId of membershipIds) {
        if (!(await store.getKeyByMembershipId(membershipId))) {
          missingMembershipIds.push(membershipId);
        }
      }
      const missingDeviceFingerprints = [];
      for (const fingerprint of deviceFingerprints) {
        if (!(await store.getKeyByFingerprint(fingerprint))) {
          missingDeviceFingerprints.push(fingerprint);
        }
      }
      return { missingMembershipIds, missingDeviceFingerprints };
    },
  };
}
